/**
 * The volume a price-breakdown row multiplies its rate by has to be the volume DC charged the row's amount
 * over; otherwise the row reads e.g. "€ 0,25133 × 0 kWh = € 226,20". With V3, DC bills consumption after
 * netting, so that volume sits on the cost detail itself (`netUsage`), not in the offer's `usages` block.
 */

export interface CostDetailVolumes {
  /** Gross volume the cost detail covers. */
  usage?: number;
  /** Volume left after netting, which DC bills when netting applies. */
  netUsage?: number;
}

export interface TaxBracketVolume {
  active?: boolean;
  usage?: number;
}

/** The volume DC billed a cost detail over, or `undefined` when DC reported none. */
export const getBilledVolume = (costDetail?: CostDetailVolumes): number | undefined =>
  costDetail?.netUsage ?? costDetail?.usage;

/** The billed volumes of several cost details added up, or `undefined` when none of them reports one. */
export const sumBilledVolumes = (...costDetails: Array<CostDetailVolumes | undefined>): number | undefined => {
  const volumes = costDetails.map(getBilledVolume).filter((volume): volume is number => volume !== undefined);

  return volumes.length ? volumes.reduce((total, volume) => total + volume, 0) : undefined;
};

/** The volume DC levied energy tax on: the usage of the active tax brackets, or `undefined` when unknown. */
export const getTaxedVolume = (brackets?: Record<string, TaxBracketVolume>): number | undefined => {
  const volumes = Object.values(brackets ?? {})
    .filter(bracket => bracket.active)
    .map(bracket => bracket.usage)
    .filter((volume): volume is number => volume !== undefined);

  return volumes.length ? volumes.reduce((total, volume) => total + volume, 0) : undefined;
};
